import { css } from '@emotion/react'
import { memo, useCallback, useRef, useState } from 'react'
import { VideoType } from '../../constants/enums'
import {
    VideoDimensions,
    VideoEventHandler,
    VideoOptions,
    register,
    unregister,
} from '../../state/state.video'
import { safeObj } from '../../utilities/safe'
import { useHealthAppStateSelector } from '../Hooks/useHealthAppState'
import { useVideoDimensions, useVideoPlaying } from '../Hooks/useVideo'
import { PAPER_HEIGHTS } from '../theme'
import PreviewPosterImage from './PreviewPosterImage'

const rootStyles = css({
    overflow: 'hidden',
    justifyContent: 'center',
    position: 'relative',
    alignItems: 'center',
    // flexBasis: '100%',
    display: 'flex',
    height: '100%',
    width: '100%',
    // flexGrow: 1,
    cursor: 'pointer',
    margin: 'auto',
})
const hideVideoStyle = {
    backgroundColor: '#000',
    zIndex: PAPER_HEIGHTS.videoBlackout,
} as const
const snapsStyle = {
    height: '100%',
    width: '100%',
} as const
const videoLowIndex = {
    display: 'none',
    zIndex: PAPER_HEIGHTS.low,
} as const

const noop = () => {}

type VideoProps = {
    onDimensions?(dimensions: VideoDimensions): void
    onPlaying?(state: boolean): void
    allowControls?: boolean
    extendedPreviewLabel?: boolean
    compactPreviewLabel?: boolean
    forceHeight?: boolean
    forefront?: boolean
    hide?: boolean
    id: number
    onclick?: VideoEventHandler
    onlyVolumeControls?: boolean
    pinToTop?: boolean
    styles?: string
    videoType: VideoType
    autoMute?: boolean
} & {
    containerStyles?: any
}

const Video = memo((props: VideoProps) => {
    const socketConnected = useHealthAppStateSelector('connected')
    const {
        extendedPreviewLabel = false,
        compactPreviewLabel = false,
        onlyVolumeControls = false,
        allowControls = true,
        forceHeight = false,
        forefront = false,
        pinToTop = false,
        containerStyles,
        onDimensions,
        videoType,
        onPlaying,
        autoMute,
        onclick,
        styles,
        hide,
        id,
    } = props
    const cleanup = useRef(noop)
    const cacheDimensions = useVideoDimensions(videoType)
    const [dimensions, setDimensions] = useState(cacheDimensions)
    const { height, width } = safeObj(dimensions)
    const playing = useVideoPlaying(id)
    const showPreviewSnaps = (!playing || !socketConnected) && !!dimensions

    // console.log('[Video]', id, [
    //     extendedPreviewLabel,
    //     compactPreviewLabel,
    //     onlyVolumeControls,
    //     allowControls,
    //     forceHeight,
    //     forefront,
    //     pinToTop,
    //     containerStyles,
    //     onDimensions,
    //     videoType,
    //     onPlaying,
    //     autoMute,
    //     onclick,
    //     styles,
    //     hide,
    // ])
    const ref = useCallback(
        (node: HTMLDivElement) => {
            // console.log(
            //     '[Video.ref]',
            //     id,
            //     [
            //         onlyVolumeControls,
            //         allowControls,
            //         onDimensions,
            //         forceHeight,
            //         onPlaying,
            //         forefront,
            //         videoType,
            //         pinToTop,
            //         autoMute,
            //         onclick,
            //         styles,
            //     ],
            //     node,
            // )
            cleanup.current()
            cleanup.current = noop

            if (!node) {
                // || (window.isIOS && !visible)) {
                return
            }

            const options: VideoOptions = {
                onlyVolumeControls,
                allowControls,
                forceHeight,
                forefront,
                pinToTop,
                autoMute,
                onclick,
                styles,
            }

            register({
                onDimensions: (dimensions: VideoDimensions) => {
                    const { height, width } = safeObj(dimensions)
                    if (height && width) {
                        // console.log('[Video.onDimensions]', id, dimensions)
                        setDimensions(dimensions)
                        onDimensions?.(dimensions)
                    }
                },
                onPlaying: (state: boolean) => {
                    onPlaying?.(state)
                },
                node,
                options,
                videoType,
                id,
            })

            cleanup.current = () => {
                setDimensions(null)
                unregister(node, id)
            }
        },
        [
            onlyVolumeControls,
            allowControls,
            onDimensions,
            forceHeight,
            onPlaying,
            forefront,
            videoType,
            pinToTop,
            autoMute,
            onclick,
            styles,
            id,
        ],
    )

    if (!id) {
        console.error('ID is required!')
        return null
    }
    return (
        <div
            id={`inline-video-container-${id}`}
            css={rootStyles}
            ref={ref}
            data-testid="Video__Container"
            style={hide ? hideVideoStyle : containerStyles}
        >
            <div style={showPreviewSnaps ? snapsStyle : videoLowIndex}>
                <PreviewPosterImage
                    compact={compactPreviewLabel}
                    extended={extendedPreviewLabel}
                    setDimensions={false}
                    staticImage={!showPreviewSnaps}
                    height={height}
                    width={width}
                    id={id}
                    large
                />
            </div>
        </div>
    )
})

export default Video
